import { isEmpty } from '@terros/common'
import { isPageRouteVisible, type RouteVisibility } from '../navigation/routeVisibility'
import { type PageRoute } from '../navigation/Navigation'

type NestedNavSource = {
  parentPath: string
  section: string
  routes: PageRoute[]
}

export const collectSpotlightDestinations = ({
  sidebarRoutes,
  nestedSources,
  isPending,
  visibility,
}: {
  sidebarRoutes: PageRoute[]
  nestedSources: NestedNavSource[]
  isPending: boolean
  visibility: RouteVisibility
}): PageRoute[] => {
  if (isPending) return []
  // Custom sidebar bookmarks are external. A nested destination can still be an external link.
  const visibleSidebar = sidebarRoutes.filter((route) => !route.isExternalLink && isPageRouteVisible(route, visibility))
  const nested = nestedSources.flatMap((source) => visibleNestedRoutes({ source, visibleSidebar, visibility }))
  return [...visibleSidebar, ...nested]
}

const visibleNestedRoutes = ({
  source,
  visibleSidebar,
  visibility,
}: {
  source: NestedNavSource
  visibleSidebar: PageRoute[]
  visibility: RouteVisibility
}): PageRoute[] => {
  const parentOpen = visibleSidebar.some((route) => route.path === source.parentPath)
  if (!parentOpen) return []
  return source.routes
    .filter((route) => isPageRouteVisible(route, visibility))
    .map((route) => ({ ...route, section: source.section }))
}

export const matchSpotlightDestinations = (destinations: PageRoute[], searchValue: string): PageRoute[] => {
  const query = searchValue.trim()
  if (isEmpty(query)) return []
  const words = query.toLocaleLowerCase().split(/\s+/)
  return destinations.filter((route) => words.every((word) => destinationSearchText(route).includes(word))).slice(0, 7)
}

const destinationSearchText = (route: PageRoute): string =>
  `${route.section ?? ''} ${route.label} ${route.path}`.toLocaleLowerCase()
